import React from 'react';
import { InlineNotification, Link } from 'carbon-components-react';
import {
  PricingSummary,
  PricingSummarySkeleton
} from 'components/PremiumWizard';
import { StripePrice } from 'redux/bulkEnquiryLodgement/types';

const HOW_IT_WORKS_ITEMS = [
  'Upload a supported geospatial file containing your work areas (GeoJSON, GeoPackage, KMZ, Shapefile (ZIP), or TAB (ZIP) formats supported).',
  "We'll validate and prepare your areas for lodgement, including automatically splitting oversized areas where required.",
  'Lodge your enquiries and download an updated file with your enquiry references attached.'
];

interface PriceStepProps {
  stripePrice: StripePrice | null;
  isLivePriceLoading: boolean;
  livePriceError: string | null;
  missingProfileAddress?: boolean;
  onOpenProfile: () => void;
}

const PriceStep: React.FC<PriceStepProps> = ({
  stripePrice,
  isLivePriceLoading,
  livePriceError,
  missingProfileAddress = false,
  onOpenProfile
}) => {
  const formattedAmount =
    (stripePrice?.formattedWithTax || stripePrice?.formatted) ?? '—';

  const total = {
    label: 'Total payable',
    amount: formattedAmount
  };

  return (
    <div className="bel-price-step">
      <div className="bel-step__header">
        Get started with large area enquiries
      </div>

      <div className="bel-price-step__section bel-price-step__notification">
        <InlineNotification
          kind="info"
          title=""
          subtitle="Lodge multiple enquiries using a geospatial data file. Ideal for projects covering large areas or multiple work sites."
          lowContrast
          hideCloseButton
        />
      </div>

      {livePriceError && (
        <div className="bel-price-step__section">
          <InlineNotification
            kind="error"
            title="Unable to load price"
            subtitle={livePriceError}
            lowContrast
            hideCloseButton
          />
        </div>
      )}

      <div className="bel-price-step__section bel-price-step__card bel-price-step__how-card">
        <div className="bel-price-step__how-heading">How it works</div>
        <ul className="bel-price-step__how-list">
          {HOW_IT_WORKS_ITEMS.map((text) => (
            <li key={text} className="bel-price-step__how-item">
              {text}
            </li>
          ))}
        </ul>
      </div>

      <div className="bel-price-step__card bel-price-step__pricing-card">
        {isLivePriceLoading ? (
          <PricingSummarySkeleton
            lineItems={[]}
            totalLabel="Total payable"
            variant="default"
          />
        ) : (
          <PricingSummary lineItems={[]} total={total} />
        )}
      </div>
      {missingProfileAddress && (
        <InlineNotification
          kind="warning"
          title=""
          subtitle={
            <>
              A postal address is required before you can continue. Update your
              postal address in{' '}
              <Link onClick={() => onOpenProfile()}>My Profile</Link>.
            </>
          }
          lowContrast
          hideCloseButton
          style={{ maxWidth: '100%' }}
        />
      )}
    </div>
  );
};

export default PriceStep;
